import React, { useCallback, useEffect, useState, useRef, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, MapPinIcon, SendIcon, XIcon, DownloadIcon, ShareIcon, CheckCircleIcon, AlertTriangleIcon, ClockIcon, PackageIcon } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { CheckoutData } from '../types/types';
import { CONFIG } from '../data/config';
import { OrderTicket } from './OrderTicket';
import { LocationPicker } from './LocationPicker';
import html2canvas from 'html2canvas';
import { useLanguage } from '../context/LanguageContext';
import { supabase } from '../lib/supabase';
interface CheckoutProps {
  onBack: () => void;
}
export function Checkout({
  onBack
}: CheckoutProps) {
  const {
    items,
    cartTotal,
    clearCart
  } = useCart();
  const {
    t,
    formatPrice
  } = useLanguage();
  const [isReturningCustomer, setIsReturningCustomer] = useState(false);
  const [data, setData] = useState<CheckoutData>(() => {
    // Check localStorage for saved customer data
    try {
      const saved = localStorage.getItem('customer_data');
      if (saved) {
        const customer = JSON.parse(saved);
        return {
          nombre: customer.name || '',
          telefono: customer.phone || '',
          email: customer.email || '',
          comentarios: '',
          alergias: '',
          entrega: 'Retiro en local' as const,
          direccion: customer.address || '',
          ubicacion: customer.location_url || '',
          metodoPago: '' as const,
          montoEfectivo: ''
        };
      }
    } catch (e) {
      console.error('Error reading customer data:', e);
    }
    return {
      nombre: '',
      telefono: '',
      email: '',
      comentarios: '',
      alergias: '',
      entrega: 'Retiro en local',
      direccion: '',
      ubicacion: '',
      metodoPago: '',
      montoEfectivo: ''
    };
  });
  const [showTicket, setShowTicket] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [ticketImage, setTicketImage] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [savedTrackingToken, setSavedTrackingToken] = useState('');
  const [whatsappUrl, setWhatsappUrl] = useState('');
  const [whatsappSent, setWhatsappSent] = useState(false);
  // Duplicate order prevention
  const [recentOrder, setRecentOrder] = useState<{
    orderNumber: string;
    trackingToken: string;
    timestamp: number;
  } | null>(() => {
    try {
      const saved = localStorage.getItem('recent_order');
      if (saved) {
        const parsed = JSON.parse(saved);
        const elapsed = Date.now() - parsed.timestamp;
        const TEN_MINUTES = 10 * 60 * 1000;
        if (elapsed < TEN_MINUTES) return parsed;
        // Expired — clean up
        localStorage.removeItem('recent_order');
      }
    } catch (e) {}
    return null;
  });
  const [dismissedRecentOrder, setDismissedRecentOrder] = useState(false);
  // Demand tracking state
  const [demandLevel, setDemandLevel] = useState<'normal' | 'high' | 'very-high'>('normal');
  const [showDemandPopup, setShowDemandPopup] = useState(false);
  const [demandAcknowledged, setDemandAcknowledged] = useState(false);
  const ticketRef = useRef<HTMLDivElement>(null);
  // Detect table number from URL
  const tableNumber = new URLSearchParams(window.location.search).get('table');
  const totalFinal = cartTotal + (!tableNumber && data.entrega === 'Express' ? CONFIG.EXPRESS_FEE : 0);
  const totalFormatted = formatPrice(totalFinal);
  // Validation: name, phone, items required; payment + delivery only for non-table orders
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const isValid = data.nombre.trim() !== '' && data.telefono.trim() !== '' && (isReturningCustomer || tableNumber || data.email.trim() !== '' && emailRegex.test(data.email)) && items.length > 0 && (tableNumber || data.metodoPago !== '') && (tableNumber || data.entrega === 'Retiro en local' || data.ubicacion !== '');
  const estimatedMinutes = tableNumber ? 15 : data.entrega === 'Express' ? 35 : 20;
  const deliveryMessage = tableNumber ? `Servido en Mesa ${tableNumber}` : data.entrega === 'Express' ? 'Entrega por servicio express' : 'Retiro en el local';
  // Check if returning customer (has saved data with email)
  useEffect(() => {
    try {
      const saved = localStorage.getItem('customer_data');
      if (saved) {
        const customer = JSON.parse(saved);
        if (customer.email) {
          setIsReturningCustomer(true);
        }
      }
    } catch (e) {
      console.error('Error checking customer data:', e);
    }
  }, []);
  // Check demand level on mount
  useEffect(() => {
    const checkDemand = async () => {
      try {
        const {
          count,
          error
        } = await supabase.from('orders').select('id', {
          count: 'exact'
        }).in('status', ['accepted', 'preparing']);
        if (error) throw error;
        const activeCount = count || 0;
        if (activeCount >= 6) {
          setDemandLevel('very-high');
          setShowDemandPopup(true);
        } else if (activeCount >= 3) {
          setDemandLevel('high');
          setShowDemandPopup(true);
        }
      } catch (err) {
        console.error('Error checking demand level:', err);
      }
    };
    checkDemand();
  }, []); // Auto-redirect to menu after 5 minutes
  useEffect(() => {
    if (!showConfirmation) return;
    const timer = setTimeout(() => {
      clearCart();
      onBack();
    }, 300000); // 5 minutes
    return () => clearTimeout(timer);
  }, [showConfirmation, clearCart, onBack]);
  const generateOrderNumber = () => {
    const now = new Date();
    const datePart = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    const timePart = `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
    const random = Math.floor(Math.random() * 100).toString().padStart(2, '0');
    return `AV-${datePart}-${timePart}${random}`;
  };
  const generateTicketImage = useCallback(async () => {
    if (!ticketRef.current) return null;
    try {
      const canvas = await html2canvas(ticketRef.current, {
        scale: 2,
        backgroundColor: '#0F0F0F',
        useCORS: true,
        allowTaint: true,
        logging: false
      });
      return canvas.toDataURL('image/png');
    } catch (err) {
      console.error('Error generating ticket image:', err);
      return null;
    }
  }, []);
  const CLOUD_NAME = 'djp4z0hdx';
  const UPLOAD_PRESET = 'tickets_restaurante';
  const dataUrlToBlob = async (dataUrl: string) => {
    const res = await fetch(dataUrl);
    return await res.blob();
  };
  const uploadTicketToCloudinary = async (dataUrl: string, orderNumber: string) => {
    const blob = await dataUrlToBlob(dataUrl);
    const formData = new FormData();
    formData.append('file', blob);
    formData.append('upload_preset', UPLOAD_PRESET);
    formData.append('folder', 'pedidos');
    formData.append('public_id', orderNumber);
    const response = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`, {
      method: 'POST',
      body: formData
    });
    const result = await response.json();
    return result.secure_url as string;
  };
  const sendOrderToMake = async ({
    orderNumber,
    imageUrl
  }: {
    orderNumber: string;
    imageUrl: string;
  }) => {
    try {
      const payload = {
        orderId: orderNumber,
        customerName: data.nombre,
        customerPhone: data.telefono,
        deliveryType: data.entrega,
        address: data.direccion,
        locationUrl: data.ubicacion,
        paymentMethod: data.metodoPago,
        subtotal: cartTotal,
        expressFee: data.entrega === 'Express' ? CONFIG.EXPRESS_FEE : 0,
        totalFinal: totalFinal,
        restaurantPhone: CONFIG.WHATSAPP_NUMBER,
        orderText: generateFullTextMessage(),
        imageUrl,
        createdAt: new Date().toISOString()
      };
      await fetch('https://hook.us2.make.com/fnw7vu6otglb9bcqj3ycm12ls8ubvbe4', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      console.log('Pedido enviado a Make con imageUrl:', imageUrl);
    } catch (error) {
      console.error('Error enviando pedido a Make:', error);
    }
  };
  const saveOrderToSupabase = async ({
    orderNumber: ordNum,
    imageUrl,
    trackingToken
  }: {
    orderNumber: string;
    imageUrl: string;
    trackingToken: string;
  }) => {
    const deliveryTypeValue = data.entrega === 'Express' ? 'express' : 'pickup';
    // Build order text inline to avoid stale state
    let orderText = `Pedido #${ordNum} — ${data.nombre} — ${data.telefono}\n`;
    orderText += `Entrega: ${data.entrega}\n`;
    items.forEach(item => {
      orderText += `${item.name} x${item.quantity} — ${CONFIG.CURRENCY_SYMBOL}${(item.subtotal * item.quantity).toLocaleString()}\n`;
      // Force-write all selections with detailed logging
      const s = item.selections;
      console.log(`🔍 Item: ${item.name}, type: ${item.type}, selections:`, JSON.stringify(s));
      if (s && typeof s === 'object') {
        if (s.base && s.base.name) orderText += `  Base: ${s.base.name}\n`;
        if (s.proteina && s.proteina.name) orderText += `  Proteina: ${s.proteina.name}\n`;
        if (Array.isArray(s.toppings) && s.toppings.length > 0) {
          const names = s.toppings.map((t: any) => t.name || t).join(', ');
          orderText += `  Toppings: ${names}\n`;
          console.log(`  ✅ Toppings written: ${names}`);
        }
        if (Array.isArray(s.toppingsPremium) && s.toppingsPremium.length > 0) {
          const names = s.toppingsPremium.map((t: any) => t.name || t).join(', ');
          orderText += `  Premium: ${names}\n`;
        }
        if (Array.isArray(s.aderezos) && s.aderezos.length > 0) {
          const names = s.aderezos.map((t: any) => t.name || t).join(', ');
          orderText += `  Aderezo: ${names}\n`;
          console.log(`  ✅ Aderezo written: ${names}`);
        }
        if (Array.isArray(s.semillas) && s.semillas.length > 0) {
          const names = s.semillas.map((t: any) => t.name || t).join(', ');
          orderText += `  Semillas: ${names}\n`;
        }
        if (s.selectedSize) orderText += `  Tamaño: ${s.selectedSize.name}\n`;
        if (s.halfAndHalf?.enabled) orderText += `  Mitad con: ${s.halfAndHalf.secondHalfName}\n`;
        if (s.comentarios) orderText += `  Notas: ${s.comentarios}\n`;
      } else {
        console.warn(`  ⚠️ No selections for ${item.name}`);
      }
    });
    console.log('📝 Final order_text:\n', orderText);
    orderText += `Total: ${CONFIG.CURRENCY_SYMBOL}${totalFinal.toLocaleString()}`;
    // Read table number from URL params
    const params = new URLSearchParams(window.location.search);
    const table = params.get('table');
    const payload = {
      order_number: ordNum,
      customer_name: data.nombre,
      customer_phone: data.telefono,
      delivery_type: deliveryTypeValue,
      address: data.direccion || null,
      location_url: data.ubicacion || null,
      payment_method: data.metodoPago,
      subtotal: cartTotal,
      express_fee: data.entrega === 'Express' ? CONFIG.EXPRESS_FEE : 0,
      total_final: totalFinal,
      image_url: imageUrl,
      order_text: orderText,
      status: 'new',
      tracking_token: trackingToken,
      table_number: table || null,
      service_mode: table ? 'qr' : null
    };
    console.log('📦 Saving order to Supabase:', JSON.stringify(payload, null, 2));
    // Try with supabase-js client first
    try {
      const {
        data: insertedOrder,
        error
      } = await supabase.from('orders').insert(payload).select();
      if (error) {
        console.error('❌ Supabase JS client error:', error);
        throw error;
      }
      console.log('✅ Order inserted via JS client:', insertedOrder);
      return insertedOrder;
    } catch (clientError) {
      console.warn('⚠️ JS client failed, trying REST API directly...');
      // Fallback: direct REST API call
      const supabaseUrl = 'https://dwyztnoyyfzyijovgrhn.supabase.co';
      const supabaseKey = 'sb_publishable_zH8IOR9Rk3skwGPF2EzYtQ_W4R4DNND';
      const response = await fetch(`${supabaseUrl}/rest/v1/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: supabaseKey,
          Authorization: `Bearer ${supabaseKey}`,
          Prefer: 'return=representation'
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ REST API error:', response.status, errorText);
        throw new Error(`REST API failed: ${response.status} ${errorText}`);
      }
      const result = await response.json();
      console.log('✅ Order inserted via REST API:', result);
      return result;
    }
  };
  const handleSendOrder = async () => {
    if (!isValid) return;
    try {
      setIsGenerating(true);
      console.log('🚀 Iniciando handleSendOrder...');
      const newOrderNumber = generateOrderNumber();
      const trackingToken = Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 6);
      setOrderNumber(newOrderNumber);
      setSavedTrackingToken(trackingToken);
      setShowConfirmation(true);
      await new Promise(resolve => setTimeout(resolve, 400));
      const imageData = await generateTicketImage();
      setTicketImage(imageData);
      if (!imageData) {
        console.error('❌ No se pudo generar la imagen del ticket');
        return;
      }
      // 1) Subir a Cloudinary
      console.log('☁️ Subiendo imagen a Cloudinary...');
      const imageUrl = await uploadTicketToCloudinary(imageData, newOrderNumber);
      console.log('✅ Cloudinary URL:', imageUrl);
      // 2) Guardar en Supabase
      try {
        console.log('💾 Guardando en Supabase...');
        await saveOrderToSupabase({
          orderNumber: newOrderNumber,
          imageUrl,
          trackingToken
        });
        console.log('✅ Pedido guardado en Supabase correctamente');
      } catch (supabaseError: any) {
        console.error('⚠️ Error guardando en Supabase:', supabaseError);
        // No mostrar error técnico al cliente — el pedido sigue por WhatsApp
      }
      // 2.5) Save/update customer in Supabase + localStorage
      try {
        const customerData = {
          name: data.nombre,
          phone: data.telefono,
          email: data.email || null,
          address: data.direccion || null,
          location_url: data.ubicacion || null
        };
        // Save to localStorage
        localStorage.setItem('customer_data', JSON.stringify(customerData));
        // Upsert to Supabase customers table
        const {
          error: customerError
        } = await supabase.from('customers').upsert({
          phone: data.telefono,
          name: data.nombre,
          email: data.email || null,
          address: data.direccion || null,
          location_url: data.ubicacion || null,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'phone'
        });
        if (customerError) {
          console.error('⚠️ Error saving customer:', customerError);
        } else {
          console.log('✅ Customer saved/updated');
          // Increment orders_count via RPC or separate update
          await supabase.rpc('increment_customer_orders', {
            customer_phone: data.telefono
          }).catch(() => {});
        }
      } catch (custErr) {
        console.error('⚠️ Error saving customer (non-critical):', custErr);
      }
      // 3) Enviar a Make (no bloquea WhatsApp si falla)
      try {
        console.log('🔗 Enviando a Make...');
        await sendOrderToMake({
          orderNumber: newOrderNumber,
          imageUrl
        });
        console.log('✅ Enviado a Make');
      } catch (makeError) {
        console.error('⚠️ Error enviando a Make (no crítico):', makeError);
      }
      // 3.5) Save recent order to prevent duplicates
      try {
        localStorage.setItem('recent_order', JSON.stringify({
          orderNumber: newOrderNumber,
          trackingToken,
          timestamp: Date.now()
        }));
      } catch (e) {}
      // 4) Construir mensaje de WhatsApp con resumen completo
      console.log('📱 Abriendo WhatsApp...');
      let whatsMsg = `🔥 *NUEVO PEDIDO — ${CONFIG.APP_NAME}*\n\n`;
      whatsMsg += `*Pedido #${newOrderNumber}*\n\n`;
      whatsMsg += `👤 *Cliente:* ${data.nombre}\n`;
      whatsMsg += `📞 *Tel:* ${data.telefono}\n\n`;
      whatsMsg += `🛒 *PEDIDO:*\n`;
      items.forEach((item, index) => {
        whatsMsg += `${index + 1}. *${item.name}* (x${item.quantity}) — ${CONFIG.CURRENCY_SYMBOL}${(item.subtotal * item.quantity).toLocaleString()}\n`;
        const s = item.selections;
        if (s.base) whatsMsg += `   Base: ${s.base.name}\n`;
        if (s.proteina) whatsMsg += `   Proteína: ${s.proteina.name}\n`;
        if (s.toppings?.length) whatsMsg += `   Toppings: ${s.toppings.map((t: any) => t.name).join(', ')}\n`;
        if (s.toppingsPremium?.length) whatsMsg += `   Premium: ${s.toppingsPremium.map((t: any) => t.name).join(', ')}\n`;
        if (s.aderezos?.length) whatsMsg += `   Aderezos: ${s.aderezos.map((t: any) => t.name).join(', ')}\n`;
        if (s.selectedSize) whatsMsg += `   Tamaño: ${s.selectedSize.name}\n`;
        if (s.halfAndHalf?.enabled) whatsMsg += `   Mitad con: ${s.halfAndHalf.secondHalfName}\n`;
        if (s.comentarios) whatsMsg += `   Notas: ${s.comentarios}\n`;
      });
      whatsMsg += `\n💰 *Subtotal:* ${CONFIG.CURRENCY_SYMBOL}${cartTotal.toLocaleString()}\n`;
      if (data.entrega === 'Express') {
        whatsMsg += `🛵 *Express:* ${CONFIG.CURRENCY_SYMBOL}${CONFIG.EXPRESS_FEE.toLocaleString()}\n`;
      }
      whatsMsg += `✅ *TOTAL: ${CONFIG.CURRENCY_SYMBOL}${totalFinal.toLocaleString()}*\n\n`;
      whatsMsg += `📦 *Entrega:* ${data.entrega === 'Express' ? 'Express 🛵' : 'Retiro en local 🏪'}\n`;
      whatsMsg += `💳 *Pago:* ${data.metodoPago}\n`;
      if (data.metodoPago === 'Efectivo' && data.montoEfectivo) {
        whatsMsg += `💵 *Paga con:* ${CONFIG.CURRENCY_SYMBOL}${data.montoEfectivo}\n`;
      }
      if (data.entrega === 'Express') {
        if (data.direccion) whatsMsg += `\n🏠 *Dirección:*\n${data.direccion}\n`;
        if (data.ubicacion) {
          whatsMsg += `\n📍 *Ubicación:*\n${data.ubicacion}\n`;
        }
      }
      if (data.comentarios) whatsMsg += `\n📝 *Notas:* ${data.comentarios}\n`;
      if (data.alergias) whatsMsg += `⚠️ *Alergias:* ${data.alergias}\n`;
      whatsMsg += `\n🧾 *Ticket:*\n${imageUrl}\n`;
      // 5) Guardar URL de WhatsApp para que el cliente lo abra cuando quiera
      const encoded = encodeURIComponent(whatsMsg);
      setWhatsappUrl(`https://wa.me/50685316690?text=${encoded}`);
      console.log('✅ handleSendOrder completado exitosamente');
    } catch (error) {
      console.error('❌ Error en handleSendOrder:', error);
    } finally {
      setIsGenerating(false);
    }
  };
  const generateFullTextMessage = () => {
    let msg = `Hola, quiero realizar este pedido en *${CONFIG.APP_NAME}* 🔥\n\n`;
    msg += `*PEDIDO #${orderNumber}*\n\n`;
    msg += `*DATOS DEL CLIENTE*\n`;
    msg += `Nombre: ${data.nombre}\n`;
    msg += `Teléfono: ${data.telefono}\n\n`;
    msg += `*TIPO DE ENTREGA*\n`;
    msg += `Entrega: ${data.entrega}\n`;
    if (data.entrega === 'Express') {
      if (data.direccion) msg += `Dirección: ${data.direccion}\n`;
      if (data.ubicacion) {
        // Extract lat,lng from the Google Maps URL
        const coordMatch = data.ubicacion.match(/q=([-\d.]+),([-\d.]+)/);
        if (coordMatch) {
          const lat = coordMatch[1];
          const lng = coordMatch[2];
          msg += `\n📍 *UBICACIÓN DEL CLIENTE*\n`;
          msg += `Google Maps: https://www.google.com/maps?q=${lat},${lng}\n`;
          msg += `Waze: https://waze.com/ul?ll=${lat},${lng}&navigate=yes\n`;
        } else {
          msg += `Ubicación: ${data.ubicacion}\n`;
        }
      }
      msg += `Cargo express: ${CONFIG.CURRENCY_SYMBOL}${CONFIG.EXPRESS_FEE}\n`;
    }
    msg += `\n`;
    msg += `*MÉTODO DE PAGO*\n`;
    msg += `Pago: ${data.metodoPago}\n`;
    if (data.metodoPago === 'Efectivo' && data.montoEfectivo) {
      msg += `Paga con: ${CONFIG.CURRENCY_SYMBOL}${data.montoEfectivo}\n`;
      const vuelto = parseInt(data.montoEfectivo) - totalFinal;
      if (vuelto > 0) msg += `Vuelto para: ${CONFIG.CURRENCY_SYMBOL}${vuelto}\n`;
    }
    msg += `\n`;
    msg += `*PEDIDO*\n`;
    items.forEach((item, index) => {
      msg += `${index + 1}. *${item.name}* (x${item.quantity})\n`;
      const s = item.selections;
      if (s.base) msg += `   - Base: ${s.base.name}\n`;
      if (s.proteina) msg += `   - Proteína: ${s.proteina.name}\n`;
      if (s.toppings?.length) msg += `   - Toppings: ${s.toppings.map(t => t.name).join(', ')}\n`;
      if (s.toppingsPremium?.length) msg += `   - Premium: ${s.toppingsPremium.map(t => t.name).join(', ')}\n`;
      if (s.aderezos?.length) msg += `   - Aderezos: ${s.aderezos.map(t => t.name).join(', ')}\n`;
      if (s.semillas?.length) msg += `   - Semillas: ${s.semillas.map(t => t.name).join(', ')}\n`;
      if (s.selectedSize) msg += `   - Tamaño: ${s.selectedSize.name}\n`;
      if (s.halfAndHalf?.enabled) msg += `   - Mitad con: ${s.halfAndHalf.secondHalfName}\n`;
      if (s.comentarios) msg += `   - Notas: ${s.comentarios}\n`;
      msg += `   - Subtotal: ${CONFIG.CURRENCY_SYMBOL}${(item.subtotal * item.quantity).toLocaleString()}\n\n`;
    });
    if (data.comentarios || data.alergias) {
      msg += `*COMENTARIOS GENERALES*\n`;
      if (data.alergias) msg += `Alergias/Restricciones: ${data.alergias}\n`;
      if (data.comentarios) msg += `Notas: ${data.comentarios}\n`;
      msg += `\n`;
    }
    msg += `*TOTAL A PAGAR*\n`;
    msg += `Subtotal: ${CONFIG.CURRENCY_SYMBOL}${cartTotal.toLocaleString()}\n`;
    if (data.entrega === 'Express') {
      msg += `Express: ${CONFIG.CURRENCY_SYMBOL}${CONFIG.EXPRESS_FEE.toLocaleString()}\n`;
    }
    msg += `*Total final: ${CONFIG.CURRENCY_SYMBOL}${totalFinal.toLocaleString()}*\n\n`;
    msg += `Gracias 🙏`;
    return msg;
  };
  const handleShareWhatsApp = async () => {
    const fullMsg = generateFullTextMessage();
    // Download ticket image to phone
    if (ticketImage) {
      const link = document.createElement('a');
      link.download = `pedido-${orderNumber}.png`;
      link.href = ticketImage;
      link.click();
    }
    // Small delay so download starts, then open WhatsApp directly to the restaurant's number
    await new Promise(resolve => setTimeout(resolve, 400));
    const encoded = encodeURIComponent(fullMsg);
    window.open(`https://wa.me/${CONFIG.WHATSAPP_NUMBER}?text=${encoded}`, '_blank');
    // Show confirmation screen
    setShowTicket(false);
    setShowConfirmation(true);
  };
  const handleDownloadTicket = () => {
    if (!ticketImage) return;
    const link = document.createElement('a');
    link.download = `pedido-${orderNumber}.png`;
    link.href = ticketImage;
    link.click();
  };
  return <motion.div initial={{
    opacity: 0,
    x: 50
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: 50
  }} className="fixed inset-0 z-40 bg-carbon flex flex-col max-w-md mx-auto" data-id="element-1060">
      <div className="bg-surface px-4 py-4 flex items-center shadow-sm z-10 border-b border-gray-800" data-id="element-1061">
        <button onClick={onBack} className="p-2 -ml-2 text-gray-400 hover:text-white" data-id="element-1062">
          <ChevronLeftIcon className="w-6 h-6" data-id="element-1063" />
        </button>
        <h2 className="font-heading font-semibold text-lg ml-2 text-white tracking-wide" data-id="element-1064">
          {tableNumber ? `🍽️ Pedido Mesa ${tableNumber}` : t('finishOrder')}
        </h2>
      </div>

      <div className="flex-1 overflow-y-auto p-4 pb-32 space-y-6" data-id="element-1065">
        {/* Duplicate Order Warning */}
        {recentOrder && !dismissedRecentOrder && !showConfirmation && <motion.section initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-amber-500/10 border border-amber-500/25 p-5 rounded-2xl" data-id="element-1066">
            <div className="flex items-start gap-3 mb-3" data-id="element-1067">
              <span className="text-2xl" data-id="element-1068">⚠️</span>
              <div data-id="element-1069">
                <h3 className="font-heading font-semibold text-amber-400 mb-1" data-id="element-1070">
                  Ya realizaste un pedido
                </h3>
                <p className="text-sm text-gray-300" data-id="element-1071">
                  Tu pedido{' '}
                  <span className="font-bold text-white" data-id="element-1072">
                    #{recentOrder.orderNumber}
                  </span>{' '}
                  está en preparación.
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-2 mt-4" data-id="element-1073">
              <a href={`${window.location.origin}${window.location.pathname}?track=${recentOrder.trackingToken}`} className="w-full py-3 bg-dorado text-carbon font-bold rounded-xl text-center text-sm hover:bg-dorado/90 transition-colors flex items-center justify-center gap-2" data-id="element-1074">
                📦 Ver estado de mi pedido
              </a>
              <button onClick={() => setDismissedRecentOrder(true)} className="w-full py-3 bg-surface-light text-gray-400 font-medium rounded-xl text-center text-sm border border-gray-700 hover:text-white hover:border-gray-600 transition-colors" data-id="element-1075">
                Quiero hacer un nuevo pedido
              </button>
            </div>
          </motion.section>}

        {/* Datos Personales */}
        <section className="bg-surface p-5 rounded-2xl shadow-sm border border-gray-800" data-id="element-1076">
          <h3 className="font-heading font-semibold text-white mb-1 tracking-wide" data-id="element-1077">
            {t('yourData')}
          </h3>
          {isReturningCustomer ? <p className="text-xs text-verde bg-verde/10 border border-verde/20 rounded-lg px-3 py-2 mb-4 flex items-center gap-2" data-id="element-1078">
              <span data-id="element-1079">👋</span> ¡Hola de nuevo, {data.nombre.split(' ')[0]}! Tus
              datos están guardados.
            </p> : <p className="text-xs text-gray-400 mb-4" data-id="element-1080">{t('dataNeeded')}</p>}
          <div className="space-y-3" data-id="element-1081">
            <div data-id="element-1082">
              <label className="block text-xs font-medium text-gray-400 mb-1" data-id="element-1083">
                {t('fullName')} <span className="text-terracota" data-id="element-1084">*</span>
              </label>
              <input type="text" value={data.nombre} onChange={e => setData({
              ...data,
              nombre: e.target.value
            })} className="w-full p-3 bg-surface-light border border-gray-700 rounded-xl focus:ring-2 focus:ring-verde outline-none text-white placeholder-gray-500" placeholder={t('namePlaceholder')} data-id="element-1085" />
            </div>
            <div data-id="element-1086">
              <label className="block text-xs font-medium text-gray-400 mb-1" data-id="element-1087">
                {t('phone')} <span className="text-terracota" data-id="element-1088">*</span>
              </label>
              <input type="tel" value={data.telefono} onChange={e => setData({
              ...data,
              telefono: e.target.value
            })} className="w-full p-3 bg-surface-light border border-gray-700 rounded-xl focus:ring-2 focus:ring-verde outline-none text-white placeholder-gray-500" placeholder={t('phonePlaceholder')} data-id="element-1089" />
              <p className="text-xs text-gray-500 mt-1" data-id="element-1090">{t('phoneHint')}</p>
            </div>
            {/* Email — only show if not already saved */}
            {!isReturningCustomer && <div data-id="element-1091">
                <label className="block text-xs font-medium text-gray-400 mb-1" data-id="element-1092">
                  Email <span className="text-terracota" data-id="element-1093">*</span>
                </label>
                <input type="text" inputMode="email" autoCapitalize="none" autoCorrect="off" value={data.email} onChange={e => setData({
              ...data,
              email: e.target.value
            })} className={`w-full p-3 bg-surface-light border rounded-xl focus:ring-2 focus:ring-verde outline-none text-white placeholder-gray-500 ${data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email) ? 'border-red-500/50' : 'border-gray-700'}`} placeholder="tu@email.com" data-id="element-1094" />
                {data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email) && <p className="text-xs text-red-400 mt-1" data-id="element-1095">
                      Ingresá un correo válido (ej: nombre@email.com)
                    </p>}
                <p className="text-xs text-gray-500 mt-1" data-id="element-1096">
                  Para enviarte ofertas y promociones 🎁
                </p>
              </div>}
          </div>
        </section>

        {/* Entrega */}
        {tableNumber ? <section className="bg-surface p-5 rounded-2xl shadow-sm border border-gray-800" data-id="element-1097">
            <h3 className="font-heading font-semibold text-white mb-1 tracking-wide" data-id="element-1098">
              {t('deliveryType')}
            </h3>
            <div className="flex items-center gap-3 mt-3 bg-dorado/10 border border-dorado/25 rounded-xl p-4" data-id="element-1099">
              <span className="text-2xl" data-id="element-1100">🍽️</span>
              <div data-id="element-1101">
                <p className="text-dorado font-bold text-lg" data-id="element-1102">
                  Mesa {tableNumber}
                </p>
                <p className="text-gray-400 text-xs" data-id="element-1103">
                  Tu pedido será servido en tu mesa
                </p>
              </div>
            </div>
          </section> : <section className="bg-surface p-5 rounded-2xl shadow-sm border border-gray-800" data-id="element-1104">
            <h3 className="font-heading font-semibold text-white mb-1 tracking-wide" data-id="element-1105">
              {t('deliveryType')}
            </h3>
            <p className="text-xs text-gray-400 mb-4" data-id="element-1106">{t('deliveryChoose')}</p>
            <div className="flex gap-3 mb-4" data-id="element-1107">
              {['Retiro en local', 'Express'].map(tipo => <button key={tipo} onClick={() => setData({
            ...data,
            entrega: tipo as any
          })} className={`flex-1 py-3 rounded-xl text-sm font-medium border-2 transition-colors ${data.entrega === tipo ? 'border-verde bg-verde/10 text-verde' : 'border-gray-700 text-gray-400 hover:bg-surface-light'}`} data-id="element-1108">
                  {tipo === 'Retiro en local' ? t('pickupLocal') : t('express')}
                </button>)}
            </div>

            {data.entrega === 'Retiro en local' && <p className="text-xs text-gray-400 bg-surface-light p-3 rounded-lg flex items-start gap-2 border border-gray-800" data-id="element-1109">
                <span className="text-base leading-none" data-id="element-1110">📍</span>
                {t('pickupHint')}
              </p>}

            {data.entrega === 'Express' && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} className="space-y-3" data-id="element-1111">
                <p className="text-xs text-terracota bg-terracota/10 p-3 rounded-lg flex items-start gap-2 border border-terracota/20" data-id="element-1112">
                  <span className="text-base leading-none" data-id="element-1113">🛵</span>
                  {t('expressCharge')} {CONFIG.CURRENCY_SYMBOL}
                  {CONFIG.EXPRESS_FEE.toLocaleString()}
                </p>

                <div data-id="element-1114">
                  <label className="block text-xs font-medium text-gray-400 mb-1" data-id="element-1115">
                    {t('exactAddress')}{' '}
                    <span className="text-xs text-gray-500 font-normal" data-id="element-1116">
                      {t('addressOptional')}
                    </span>
                  </label>
                  <textarea value={data.direccion} onChange={e => setData({
              ...data,
              direccion: e.target.value
            })} className="w-full p-3 bg-surface-light border border-gray-700 rounded-xl focus:ring-2 focus:ring-verde outline-none resize-none h-20 text-white placeholder-gray-500" placeholder={t('addressPlaceholder')} data-id="element-1117" />
                </div>
                <div className="pt-2" data-id="element-1118">
                  <label className="block text-xs font-medium text-gray-400 mb-2" data-id="element-1119">
                    {t('mapLocation')}{' '}
                    <span className="text-xs text-gray-500 font-normal" data-id="element-1120">
                      {t('mapRecommended')}
                    </span>
                  </label>
                  <LocationPicker onConfirm={url => setData({
              ...data,
              ubicacion: url
            })} initialLocation={data.ubicacion} data-id="element-1121" />
                </div>
              </motion.div>}
          </section>}

        {/* Pago */}
        {tableNumber ? <section className="bg-surface p-5 rounded-2xl shadow-sm border border-gray-800" data-id="element-1122">
            <h3 className="font-heading font-semibold text-white mb-1 tracking-wide" data-id="element-1123">
              {t('paymentMethod')}
            </h3>
            <div className="flex items-center gap-3 mt-3 bg-verde/10 border border-verde/25 rounded-xl p-4" data-id="element-1124">
              <span className="text-2xl" data-id="element-1125">💳</span>
              <div data-id="element-1126">
                <p className="text-verde font-bold" data-id="element-1127">Pago en mesa</p>
                <p className="text-gray-400 text-xs" data-id="element-1128">
                  Coordiná el pago directamente con tu mesero
                </p>
              </div>
            </div>
          </section> : <section className="bg-surface p-5 rounded-2xl shadow-sm border border-gray-800" data-id="element-1129">
            <h3 className="font-heading font-semibold text-white mb-1 tracking-wide" data-id="element-1130">
              {t('paymentMethod')}
            </h3>
            <p className="text-xs text-gray-400 mb-4" data-id="element-1131">{t('paymentChoose')}</p>
            <div className="grid grid-cols-3 gap-2 mb-4" data-id="element-1132">
              {['SINPE', 'Tarjeta', 'Efectivo'].map(tipo => <button key={tipo} onClick={() => setData({
            ...data,
            metodoPago: tipo as any
          })} className={`py-3 rounded-xl text-sm font-medium border-2 transition-colors ${data.metodoPago === tipo ? 'border-verde bg-verde/10 text-verde' : 'border-gray-700 text-gray-400 hover:bg-surface-light'}`} data-id="element-1133">
                  {tipo === 'Tarjeta' ? t('card') : tipo === 'Efectivo' ? t('cash') : tipo}
                </button>)}
            </div>

            {data.metodoPago === 'SINPE' && <p className="text-xs text-gray-400 bg-surface-light p-3 rounded-lg border border-gray-800" data-id="element-1134">
                {t('sinpeHint')}
              </p>}
            {data.metodoPago === 'Tarjeta' && <p className="text-xs text-gray-400 bg-surface-light p-3 rounded-lg border border-gray-800" data-id="element-1135">
                {t('cardHint')}
              </p>}

            {data.metodoPago === 'Efectivo' && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} data-id="element-1136">
                <label className="block text-xs font-medium text-gray-400 mb-1" data-id="element-1137">
                  {t('payWith')}{' '}
                  <span className="text-xs text-gray-500 font-normal" data-id="element-1138">
                    {t('payWithOptional')}
                  </span>
                </label>
                <input type="number" value={data.montoEfectivo} onChange={e => setData({
            ...data,
            montoEfectivo: e.target.value
          })} className="w-full p-3 bg-surface-light border border-gray-700 rounded-xl focus:ring-2 focus:ring-verde outline-none text-white placeholder-gray-500" placeholder={t('payWithPlaceholder')} data-id="element-1139" />
                {data.montoEfectivo && parseInt(data.montoEfectivo) > totalFinal && <p className="text-sm text-gray-400 mt-2" data-id="element-1140">
                      {t('estimatedChange')}{' '}
                      <span className="font-semibold text-white" data-id="element-1141">
                        {CONFIG.CURRENCY_SYMBOL}
                        {parseInt(data.montoEfectivo) - totalFinal}
                      </span>
                    </p>}
                {!data.montoEfectivo && <p className="text-xs text-gray-500 mt-1" data-id="element-1142">
                    {t('changeHint')}
                  </p>}
              </motion.div>}
          </section>}

        {/* Extras */}
        <section className="bg-surface p-5 rounded-2xl shadow-sm border border-gray-800" data-id="element-1143">
          <div className="flex items-center gap-2 mb-1" data-id="element-1144">
            <h3 className="font-heading font-semibold text-white tracking-wide" data-id="element-1145">
              {t('additionalComments')}
            </h3>
            <span className="text-xs font-medium bg-surface-light text-gray-400 px-2 py-0.5 rounded-full border border-gray-700" data-id="element-1146">
              {t('optional')}
            </span>
          </div>
          <p className="text-xs text-gray-400 mb-4" data-id="element-1147">{t('noCommentsHint')}</p>
          <div className="space-y-3" data-id="element-1148">
            <div data-id="element-1149">
              <label className="block text-xs font-medium text-red-400 mb-1" data-id="element-1150">
                {t('allergies')}
              </label>
              <input type="text" value={data.alergias} onChange={e => setData({
              ...data,
              alergias: e.target.value
            })} className="w-full p-3 bg-red-950/20 border border-red-900/50 rounded-xl focus:ring-2 focus:ring-red-500 outline-none placeholder-red-900/50 text-red-400" placeholder={t('allergiesPlaceholder')} data-id="element-1151" />
              <p className="text-xs text-red-500/70 mt-1" data-id="element-1152">
                {t('allergiesWarning')}
              </p>
            </div>
            <div data-id="element-1153">
              <label className="block text-xs font-medium text-gray-400 mb-1" data-id="element-1154">
                {t('restaurantNotes')}
              </label>
              <textarea value={data.comentarios} onChange={e => setData({
              ...data,
              comentarios: e.target.value
            })} className="w-full p-3 bg-surface-light border border-gray-700 rounded-xl focus:ring-2 focus:ring-verde outline-none resize-none h-20 text-white placeholder-gray-500" placeholder={t('restaurantNotesPlaceholder')} data-id="element-1155" />
            </div>
          </div>
        </section>
      </div>

      {/* Footer Sticky */}
      <div className="bg-surface border-t border-gray-800 p-4 pb-safe fixed bottom-0 left-0 right-0 max-w-md mx-auto z-20" data-id="element-1156">
        <div className="flex justify-between items-center mb-3 px-2" data-id="element-1157">
          <span className="text-gray-400 font-medium" data-id="element-1158">{t('totalToPay')}</span>
          <div className="flex flex-col items-end" data-id="element-1159">
            <span className="font-bold text-2xl text-white" data-id="element-1160">
              {totalFormatted.primary}
            </span>
            <span className="text-sm text-gray-400" data-id="element-1161">
              {totalFormatted.secondary}
            </span>
          </div>
        </div>
        <button onClick={handleSendOrder} disabled={!isValid || isGenerating} className={`w-full py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all border border-white/10 ${isValid && !isGenerating ? tableNumber ? 'bg-dorado text-carbon hover:bg-dorado/90 shadow-lg shadow-dorado/20 active:scale-95' : 'bg-[#25D366] text-white hover:bg-[#1ebd5a] shadow-lg shadow-[#25D366]/20 active:scale-95' : 'bg-gray-800 text-gray-500 cursor-not-allowed'}`} data-id="element-1162">
          {isGenerating ? <>
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1163" />
              {t('generatingTicket')}
            </> : tableNumber ? <>🍽️ Enviar pedido a cocina</> : <>
              <SendIcon className="w-5 h-5" data-id="element-1164" />
              Enviar mi pedido por WhatsApp
            </>}
        </button>
        {!isValid && <p className="text-center text-xs text-red-500 mt-2" data-id="element-1165">
            {data.nombre.trim() === '' && t('completeName') + ' '}
            {data.telefono.trim() === '' && t('addPhone') + ' '}
            {items.length === 0 && t('addProduct') + ' '}
            {!tableNumber && data.metodoPago === '' && '• Seleccioná un método de pago '}
            {!tableNumber && data.entrega === 'Express' && data.ubicacion === '' && '• Marcá tu ubicación en el mapa '}
          </p>}
      </div>

      {/* Hidden ticket for rendering */}
      <div style={{
      position: 'absolute',
      left: '-9999px',
      top: 0
    }} data-id="element-1166">
        <OrderTicket ref={ticketRef} orderNumber={orderNumber} data={data} items={items} cartTotal={cartTotal} totalFinal={totalFinal} data-id="element-1167" />
      </div>

      {/* Demand Popups */}
      <AnimatePresence data-id="element-1168">
        {showDemandPopup && !demandAcknowledged && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[60] bg-carbon/90 backdrop-blur-sm flex items-center justify-center p-4" data-id="element-1169">
            <motion.div initial={{
          scale: 0.9,
          y: 20,
          opacity: 0
        }} animate={{
          scale: 1,
          y: 0,
          opacity: 1
        }} exit={{
          scale: 0.9,
          y: 20,
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 25
        }} className={`bg-surface border rounded-2xl p-6 max-w-sm w-full shadow-2xl ${demandLevel === 'very-high' ? 'border-red-500/30' : 'border-amber-500/30'}`} data-id="element-1170">
              <div className="flex flex-col items-center text-center" data-id="element-1171">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 border-2 ${demandLevel === 'very-high' ? 'bg-red-500/10 border-red-500/20 text-red-500' : 'bg-amber-500/10 border-amber-500/20 text-amber-500'}`} data-id="element-1172">
                  {demandLevel === 'very-high' ? <AlertTriangleIcon className="w-8 h-8" data-id="element-1173" /> : <ClockIcon className="w-8 h-8" data-id="element-1174" />}
                </div>

                <h3 className="font-heading text-xl text-white mb-2" data-id="element-1175">
                  {demandLevel === 'very-high' ? '¡Cocina al máximo! 🔥' : 'Alta demanda 🔥'}
                </h3>

                <p className="text-gray-400 text-sm mb-6 leading-relaxed" data-id="element-1176">
                  {demandLevel === 'very-high' ? 'Tenemos muchos pedidos en este momento. Tu pedido puede demorar hasta 45 minutos. ¿Deseas continuar?' : 'Tenemos muchos pedidos en este momento. Tu pedido puede demorar un poco más de lo habitual.'}
                </p>

                <div className="flex flex-col w-full gap-3" data-id="element-1177">
                  <button onClick={() => {
                setDemandAcknowledged(true);
                setShowDemandPopup(false);
              }} className={`w-full py-3 rounded-xl font-semibold transition-colors ${demandLevel === 'very-high' ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-amber-500 hover:bg-amber-600 text-white'}`} data-id="element-1178">
                    {demandLevel === 'very-high' ? 'Sí, continuar' : 'Entendido, continuar'}
                  </button>

                  {demandLevel === 'very-high' && <button onClick={() => {
                clearCart();
                onBack();
              }} className="w-full py-3 rounded-xl font-medium text-gray-400 hover:text-white hover:bg-white/5 transition-colors" data-id="element-1179">
                      Abandonar pedido
                    </button>}
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Ticket Preview Modal */}
      <AnimatePresence data-id="element-1180">
        {showTicket && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowTicket(false)} className="fixed inset-0 bg-black/80 z-50 backdrop-blur-sm" data-id="element-1181" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.9,
          y: 40
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.9,
          y: 40
        }} transition={{
          type: 'spring',
          damping: 25
        }} className="fixed inset-x-4 top-8 bottom-8 z-50 flex flex-col items-center max-w-md mx-auto" data-id="element-1182">
              {/* Close button */}
              <button onClick={() => setShowTicket(false)} className="absolute top-2 right-2 z-10 bg-surface rounded-full p-2 shadow-md border border-gray-700" data-id="element-1183">
                <XIcon className="w-5 h-5 text-gray-300" data-id="element-1184" />
              </button>

              {/* Ticket image preview */}
              <div className="flex-1 overflow-y-auto w-full flex justify-center py-4" data-id="element-1185">
                {ticketImage ? <img src={ticketImage} alt="Ticket de pedido" className="w-full max-w-[340px] rounded-2xl shadow-2xl object-contain" data-id="element-1186" /> : <div className="flex items-center justify-center h-full" data-id="element-1187">
                    <div className="text-white text-center" data-id="element-1188">
                      <div className="w-8 h-8 border-2 border-verde border-t-transparent rounded-full animate-spin mx-auto mb-3" data-id="element-1189" />
                      <p className="text-sm" data-id="element-1190">{t('generatingTicket')}</p>
                    </div>
                  </div>}
              </div>

              {/* Success message + Action buttons */}
              {ticketImage && <div className="w-full pt-3 pb-2 px-2 space-y-3" data-id="element-1191">
                  <div className="flex items-center justify-center gap-2 py-2" data-id="element-1192">
                    <CheckCircleIcon className="w-5 h-5 text-[#25D366]" data-id="element-1193" />
                    <p className="text-sm text-gray-300 font-medium" data-id="element-1194">
                      Tu pedido fue enviado correctamente
                    </p>
                  </div>
                  <div className="flex gap-3" data-id="element-1195">
                    <button onClick={handleDownloadTicket} className="flex-1 bg-surface-light text-white font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 shadow-md active:scale-95 transition-transform border border-gray-700" data-id="element-1196">
                      <DownloadIcon className="w-5 h-5" data-id="element-1197" />
                      {t('save')}
                    </button>
                    <button onClick={() => {
                setShowTicket(false);
                setShowConfirmation(true);
              }} className="flex-[2] bg-terracota text-white font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-terracota/20 active:scale-95 transition-transform border border-white/10" data-id="element-1198">
                      <XIcon className="w-5 h-5" data-id="element-1199" />
                      Cerrar
                    </button>
                  </div>
                </div>}
            </motion.div>
          </>}
      </AnimatePresence>

      {/* Redirecting to WhatsApp Screen */}
      <AnimatePresence data-id="element-1200">
        {showConfirmation && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[60] bg-carbon flex flex-col items-center justify-center max-w-md mx-auto px-8" data-id="element-1201">
            <motion.div initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          type: 'spring',
          damping: 15,
          delay: 0.2
        }} className="mb-6" data-id="element-1202">
              <div className="w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center border-2 border-emerald-500/30" data-id="element-1203">
                <CheckCircleIcon className="w-10 h-10 text-emerald-500" data-id="element-1204" />
              </div>
            </motion.div>

            <motion.div initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.4
        }} className="text-center space-y-2" data-id="element-1205">
              <h2 className="font-heading text-2xl text-white tracking-wide" data-id="element-1206">
                {tableNumber ? '¡Pedido enviado a cocina!' : '¡Pedido registrado!'}
              </h2>
              <p className="text-gray-400 text-sm" data-id="element-1207">Pedido #{orderNumber}</p>
              {tableNumber && <p className="text-dorado text-sm font-medium mt-1" data-id="element-1208">
                  🍽️ Mesa {tableNumber} · ~{estimatedMinutes} min
                </p>}
            </motion.div>

            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6
        }} className="mt-8 w-full max-w-xs flex flex-col items-center gap-3" data-id="element-1209">
              {/* Table orders: direct confirmation, no WhatsApp step */}
              {tableNumber ? <div className="w-full bg-dorado/10 border border-dorado/25 rounded-xl p-5" data-id="element-1210">
                  <p className="text-white font-bold text-lg mb-2" data-id="element-1211">
                    ¡Tu pedido está en camino! 🔥
                  </p>
                  <p className="text-gray-400 text-sm leading-relaxed" data-id="element-1212">
                    La cocina ya recibió tu pedido. Te lo llevaremos a tu mesa
                    en aproximadamente {estimatedMinutes} minutos.
                  </p>
                </div> : !whatsappSent ? <div className="w-full bg-surface-light border border-gray-700 rounded-xl p-5" data-id="element-1213">
                  <p className="text-white font-bold text-lg mb-4" data-id="element-1214">
                    Para completar tu pedido:
                  </p>
                  <div className="space-y-3" data-id="element-1215">
                    <div className="flex items-start gap-3" data-id="element-1216">
                      <span className="w-6 h-6 bg-[#25D366]/20 text-[#25D366] rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5" data-id="element-1217">
                        1
                      </span>
                      <p className="text-gray-300 text-sm" data-id="element-1218">
                        Tocá el botón{' '}
                        <span className="text-[#25D366] font-semibold" data-id="element-1219">
                          Enviar por WhatsApp
                        </span>
                      </p>
                    </div>
                    <div className="flex items-start gap-3" data-id="element-1220">
                      <span className="w-6 h-6 bg-gray-600/30 text-gray-500 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5" data-id="element-1221">
                        2
                      </span>
                      <p className="text-gray-500 text-sm" data-id="element-1222">
                        Volvé aquí y aparecerá el link para rastrear tu pedido
                      </p>
                    </div>
                  </div>
                </div> : <div className="w-full bg-surface-light border border-dorado/30 rounded-xl p-5" data-id="element-1223">
                  <p className="text-white font-bold text-lg mb-2" data-id="element-1224">
                    ¡Pedido enviado! 🎉
                  </p>
                  <p className="text-gray-400 text-sm" data-id="element-1225">
                    Ahora podés rastrear el estado de tu pedido en tiempo real.
                  </p>
                </div>}

              {/* Step 1: Send to restaurant via WhatsApp (only for non-table orders) */}
              {!tableNumber && whatsappUrl && !whatsappSent && <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" onClick={() => setWhatsappSent(true)} className="w-full px-6 py-4 bg-[#25D366] text-white font-bold rounded-xl flex items-center justify-center gap-3 active:scale-95 transition-transform shadow-lg shadow-[#25D366]/20" data-id="element-1226">
                  <SendIcon className="w-5 h-5" data-id="element-1227" />
                  Enviar por WhatsApp
                </a>}

              {/* Table orders: show tracking immediately */}
              {tableNumber && savedTrackingToken && <>
                  <button onClick={() => {
              clearCart();
              window.location.href = `/track/${savedTrackingToken}`;
            }} className="w-full px-6 py-4 bg-dorado text-carbon font-bold rounded-xl flex items-center justify-center gap-2 active:scale-95 transition-transform shadow-lg shadow-dorado/20" data-id="element-1228">
                    <PackageIcon className="w-5 h-5" data-id="element-1229" />
                    Ver estado de mi pedido
                  </button>
                  <button onClick={() => {
              clearCart();
              onBack();
            }} className="w-full px-4 py-3 bg-surface border border-gray-700 text-gray-300 font-medium rounded-xl hover:bg-surface-light transition-colors text-sm" data-id="element-1230">
                    Pedir algo más para la mesa
                  </button>
                </>}

              {/* Non-table: After WhatsApp sent, show tracking */}
              {!tableNumber && whatsappSent && savedTrackingToken && <>
                  <div className="w-full bg-[#25D366]/10 border border-[#25D366]/30 rounded-xl p-3 text-center" data-id="element-1231">
                    <p className="text-[#25D366] font-semibold text-sm" data-id="element-1232">
                      ✅ ¡Listo! Ahora podés rastrear tu pedido
                    </p>
                  </div>
                  <button onClick={() => {
              clearCart();
              window.location.href = `/track/${savedTrackingToken}`;
            }} className="w-full px-6 py-4 bg-dorado text-carbon font-bold rounded-xl flex items-center justify-center gap-2 active:scale-95 transition-transform shadow-lg shadow-dorado/20" data-id="element-1233">
                    <PackageIcon className="w-5 h-5" data-id="element-1234" />
                    Rastrear mi pedido
                  </button>
                </>}

              {!tableNumber && <div className="w-full flex gap-3 mt-4" data-id="element-1235">
                  <button onClick={() => window.close()} className="flex-1 px-4 py-3 bg-surface border border-gray-700 text-gray-300 font-medium rounded-xl hover:bg-surface-light transition-colors text-sm" data-id="element-1236">
                    Cerrar navegador
                  </button>
                  <button onClick={() => {
              clearCart();
              onBack();
            }} className="flex-1 px-4 py-3 bg-surface border border-gray-700 text-gray-300 font-medium rounded-xl hover:bg-surface-light transition-colors text-sm" data-id="element-1237">
                    Pedir algo más
                  </button>
                </div>}
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}